import { useRef, type MutableRefObject, type PointerEvent as ReactPointerEvent } from 'react'
import { TraceChart, traceScale, type TraceSeries } from './TraceChart'
import type { Trace } from './traces'

/**
 * Eine Spur als Zeile: Kopfzeile, Bild, Fusszeile.
 *
 * DREI ZEILEN UND KEINE KARTE. Eine Karte mit Rahmen, Radius und Innenabstand
 * kostet je Spur rund vierzig Pixel Rand — bei sechs Spuren ist das eine ganze
 * Spur an Rahmen. Hier trennt eine Haarlinie, und was übrig bleibt, ist Inhalt.
 *
 * WAS IN DER KOPFZEILE STEHT: links der Griff, in der Mitte je Kanal eine Marke
 * mit Farbe, Name und dem abgelesenen Wert, rechts die Knöpfe. Der Wert an der
 * Marke ist der Grund, warum die Spur kein beschriftetes Diagramm braucht — man
 * liest ihn dort, wo auch der Name steht.
 *
 * DIE KNÖPFE SIND DREI VERSCHIEDENE DINGE:
 *
 *   Teilen      — die überlagerten Kanäle wieder auseinandernehmen. Steht nur
 *                 da, wo es mehr als einen gibt.
 *   Ausblenden  — die Spur wird stumm, die Auswahl bleibt. Wer sechs Spuren
 *                 aufgebaut hat, soll eine nicht abräumen und neu
 *                 zusammensuchen müssen.
 *   Entfernen   — Spur und Kanäle sind weg.
 *
 * ZIEHEN MACHT ZWEIERLEI, und das ist der Kern der Bedienung: über den Rand
 * einer anderen Spur gezogen sortiert es um, in ihre MITTE gezogen legt es die
 * beiden übereinander. Ein zweiter Bedienweg fürs Überlagern wäre eine zweite
 * Stelle, an der man es suchen muss.
 */

export interface TracePanelProps {
  trace: Trace
  index: number
  series: TraceSeries[]
  height: number
  stance?: number
  cursor?: MutableRefObject<number>
  /** Nachkommastellen je Kanal. */
  decimals?: Record<string, number>
  onSplit: () => void
  onToggleVisible: () => void
  onClose: () => void
  onRemoveChannel: (key: string) => void
  onScrub?: (at: number) => void
  /** Zieht die Spur an: der Rest läuft über die Zeiger-Ereignisse der Seite. */
  onGrab: (event: ReactPointerEvent, index: number) => void
  /** Umsortieren mit der Tastatur — Ziehen ist ohne Maus unmöglich. */
  onMove: (from: number, to: number) => void
  /** Liegt gerade eine gezogene Spur über der Mitte dieser hier? */
  mergeTarget?: boolean
  dragging?: boolean
}

export function TracePanel({
  trace,
  index,
  series,
  height,
  stance,
  cursor,
  decimals,
  onSplit,
  onToggleVisible,
  onClose,
  onRemoveChannel,
  onScrub,
  onGrab,
  onMove,
  mergeTarget,
  dragging,
}: TracePanelProps) {
  /*
    Die Knoten der Werte werden dem Bild gereicht und nicht als Zustand
    geführt: der abgelesene Wert ändert sich sechzigmal je Sekunde, und ein
    Zustand daraus wären sechzig Renderdurchläufe je Sekunde und Spur.
  */
  const readouts = useRef<Record<string, HTMLElement | null>>({})
  const scale = traceScale(series)

  const foot = scale.mixed
    ? series
        .map((s) => {
          const [lo, hi] = scale.per[s.key] ?? [0, 0]
          return `${s.label} ${trim(lo)}…${trim(hi)} ${s.unit}`.trim()
        })
        .join('  ·  ')
    : `${trim(scale.lo)} … ${trim(scale.hi)} ${scale.unit}`.trim()

  return (
    <div
      className={[
        'trace',
        trace.visible ? '' : 'trace--off',
        mergeTarget ? 'trace--merge' : '',
        dragging ? 'trace--dragging' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      data-index={index}
    >
      <div className="trace__head">
        <span
          className="trace__grip"
          onPointerDown={(event) => onGrab(event, index)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowUp') {
              event.preventDefault()
              onMove(index, index - 1)
            }
            if (event.key === 'ArrowDown') {
              event.preventDefault()
              onMove(index, index + 1)
            }
          }}
          role="button"
          tabIndex={0}
          title="Ziehen: umsortieren — auf die Mitte einer anderen Spur: überlagern"
          aria-label="Spur umsortieren"
        >
          <GripIcon />
        </span>

        <div className="trace__chips">
          {series.map((s) => (
            <span key={s.key} className="cchip">
              <i className="cchip__tone" style={{ background: `var(--chart-${s.tone})` }} />
              {s.label}
              <b
                ref={(el) => {
                  readouts.current[s.key] = el
                }}
                className="cchip__value numeric"
              >
                –
              </b>
              <button
                type="button"
                className="cchip__off"
                onClick={() => onRemoveChannel(s.key)}
                title={`${s.label} aus der Spur nehmen`}
                aria-label={`${s.label} aus der Spur nehmen`}
              >
                ×
              </button>
            </span>
          ))}
        </div>

        <div className="trace__actions">
          {series.length > 1 && (
            <button type="button" className="trace__button" onClick={onSplit} title="Spur teilen">
              <SplitIcon />
            </button>
          )}
          <button
            type="button"
            className="trace__button"
            onClick={onToggleVisible}
            title={trace.visible ? 'Ausblenden' : 'Wieder zeigen'}
            aria-pressed={!trace.visible}
          >
            {trace.visible ? <EyeIcon /> : <EyeOffIcon />}
          </button>
          <button
            type="button"
            className="trace__button"
            onClick={onClose}
            title="Spur entfernen"
            aria-label="Spur entfernen"
          >
            <CloseIcon />
          </button>
        </div>
      </div>

      <TraceChart
        series={series}
        height={height}
        stance={stance}
        cursor={trace.visible ? cursor : undefined}
        readouts={readouts}
        decimals={decimals}
        onScrub={onScrub}
      />

      <div className="trace__foot">
        <span className="trace__range">{scale.mixed ? 'normiert je Kanal' : foot}</span>
        <span className="trace__span">{scale.mixed ? foot : '0 – 100 % Gangzyklus'}</span>
      </div>
    </div>
  )
}

/** Höchstens zwei Nachkommastellen und keine leeren Nullen. */
const trim = (v: number): string => String(Number(v.toFixed(2)))

/* -------------------------------------------------------------------------
   Zeichen
   ------------------------------------------------------------------------- */

const icon = { width: 13, height: 13, viewBox: '0 0 16 16', 'aria-hidden': true } as const

const GripIcon = () => (
  <svg {...icon} fill="currentColor">
    <circle cx="6" cy="4" r="1.2" />
    <circle cx="10" cy="4" r="1.2" />
    <circle cx="6" cy="8" r="1.2" />
    <circle cx="10" cy="8" r="1.2" />
    <circle cx="6" cy="12" r="1.2" />
    <circle cx="10" cy="12" r="1.2" />
  </svg>
)

const SplitIcon = () => (
  <svg {...icon} fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
    <path d="M2.5 5.5h11M2.5 10.5h11" />
    <path d="M6 3 8 5.5 6 8M10 8l-2 2.5 2 2.5" />
  </svg>
)

const EyeIcon = () => (
  <svg {...icon} fill="none" stroke="currentColor" strokeWidth="1.4">
    <path d="M1.5 8s2.4-4 6.5-4 6.5 4 6.5 4-2.4 4-6.5 4-6.5-4-6.5-4z" />
    <circle cx="8" cy="8" r="1.8" />
  </svg>
)

const EyeOffIcon = () => (
  <svg {...icon} fill="none" stroke="currentColor" strokeWidth="1.4">
    <path d="M1.5 8s2.4-4 6.5-4c1.2 0 2.2.3 3.1.8M14.5 8s-2.4 4-6.5 4c-1.2 0-2.2-.3-3.1-.8" />
    <path d="M2.5 2.5l11 11" />
  </svg>
)

const CloseIcon = () => (
  <svg {...icon} fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
    <path d="m4.5 4.5 7 7M11.5 4.5l-7 7" />
  </svg>
)
